import {
  Button,
  Card,
  Container,
  Group,
  Image,
  Stack,
  Text,
  Title,
  SimpleGrid,
  Modal,
  Badge,
  Divider,
  Alert,
  Center,
} from "@mantine/core";
import { Grid2Props as GridProps, Grid2 as MuiGrid } from "@mui/material";
import { IconCheck, IconClock, IconUsers, IconStar, IconBook } from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MegaLoader from "../../Components/MegaLoader";
import TopNav from "../../Components/TopNav";
import { Colors } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/responses";
import { CourseProps, PlanProps, StudentProps } from "../../Lib/types";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import {
  DefaultErrorNotification,
  getCurrencySymbol,
  getFinancialValueFromNumeric,
} from "../../Lib/methods";

import "./styles.scss";
import { getEllipsisFromString } from "../../Lib/methods";
import { AppContext } from "../Dashboard";
import { AllFatherContext } from "../../App";
import { useDisclosure } from "@mantine/hooks";
const PlansNoAuth = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const coursesSectionRef = useRef<HTMLDivElement>(null);
  const context = useContext(AppContext);
  const allFatherContext = useContext(AllFatherContext);

  const [selectedPlan, setSelectedPlan] = useState<string | null>("");

  const [selectedCourse, setSelectedCourse] = useState<CourseProps | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const scrollToCourses = () => {
    if (coursesSectionRef.current) {
      coursesSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const { data: plans, isLoading: isLoadingPlans } = usePerformRequest<
    NonPaginatedResponse<PlanProps[]>
  >({
    method: Endpoints.GetPlans(allFatherContext?.locationInformation?.currency).method,
    route: Endpoints.GetPlans(allFatherContext?.locationInformation?.currency).route,
  });

  const {
    data: courses,
    isLoading: isLoadingCourses,
    reloadData: reloadCourses,
  } = usePerformRequest<PaginatedResponse<CourseProps[]>>({
    method: Endpoints.GetCourses(1, 30).method,
    route: Endpoints.GetCourses(1, 30).route,
    body: selectedPlan ? { planID: selectedPlan } : {},
    showError: false,
  });

  useEffect(() => {
    if (selectedPlan) {
      reloadCourses();
    }
  }, [selectedPlan]);

  const {
    data: studentProfile,
    response: studentProfileResponse,
    reloadData: reloadStudentProfile,
    isLoading: isLoadingProfile,
  } = usePerformRequest<NonPaginatedResponse<StudentProps>>({
    method: Endpoints.GetStudentProfile().method,
    route: Endpoints.GetStudentProfile().route,
  });

  const studentPlans: string[] = studentProfile?.data?.enrolledPlans ?? [];
  const gridProps: GridProps = {
    size: {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 4,
      xl: 3,
    },
  };

  const RouteToSubsribe = (planID?: string) => {
    if (studentProfile?.data) {
      // Check if student is already enrolled in this plan
      if (studentPlans.includes(selectedPlan!)) {
        DefaultErrorNotification("You are already enrolled in this plan!");
        return;
      }
      navigate(`/d/purchase-plan/${planID}`);
    } else {
      navigate(`/login?redirect=/d/purchase-plan/${planID}`);
    }
  };

  const [isPlanDetailsModalOpened, { open: OpenPlanDetailsModal, close: ClosePlanDetailsModal }] =
    useDisclosure(false);

  return (
    <>
      <TopNav />
      <div className="plans-no-auth-container">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="hero-section">
            <div className="hero-content">
              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {t("home.hero.title")}
              </motion.h1>
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {t("home.hero.subtitle")}
              </motion.p>
            </div>
          </div>

          <div className="plans-section">
            <Container size="lg">
              {isLoadingPlans ? (
                <MegaLoader />
              ) : (
                <div className="plans-grid">
                  <MuiGrid {...gridProps} container spacing={3}>
                    {plans?.data?.map((plan) => (
                      <MuiGrid key={plan.id} {...gridProps}>
                        <Card className="plan-card">
                          <div className="plan-header">
                            <div className="plan-title-section">
                              <Title order={3}>{plan.name}</Title>
                              <div className="flex-row align-center justify-between width-100">
                                <Text size="sm" c="dimmed">
                                  {plan.tag}
                                </Text>
                                {studentProfile?.data?.enrolledPlans.includes(plan.id) && (
                                  <Badge color="green" variant="light">
                                    Enrolled
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="plan-icon">
                              <IconBook size={20} />
                            </div>
                          </div>
                          <div className="plan-price">
                            <div className="flex-col">
                              <Text fz={30} fw={600}>
                                {getCurrencySymbol(allFatherContext?.locationInformation?.currency)}
                                {getFinancialValueFromNumeric(plan.price)}
                              </Text>
                              <Text fz={14} fw={500} mt={-5} c={Colors.GrayPrimary}>
                                per month
                              </Text>
                            </div>
                          </div>
                          <div className="plan-footer">
                            <Button
                              fullWidth
                              size="md"
                              color={Colors.PurplePrimary}
                              variant={plan.id === selectedPlan ? "filled" : "outline"}
                              onClick={() => {
                                if (studentProfile?.data) {
                                  if (studentPlans.includes(plan.id)) {
                                    navigate(`/d/account`);
                                  } else {
                                    if (selectedPlan === plan.id) {
                                      RouteToSubsribe(plan.id);
                                    } else {
                                      setSelectedPlan(plan.id);
                                      scrollToCourses();
                                    }
                                  }
                                } else {
                                  navigate(`/login?redirect=/d/purchase-plan/${plan.id}`);
                                }
                              }}
                            >
                              {studentProfile?.data?.enrolledPlans.includes(plan.id)
                                ? "Manage"
                                : plan.id === selectedPlan
                                ? "Purchase"
                                : "Select plan"}
                            </Button>
                            <Center mt={10} onClick={OpenPlanDetailsModal}>
                              <Text
                                onClick={() => {
                                  setSelectedPlan(plan.id);
                                  OpenPlanDetailsModal();
                                }}
                                className="pointer"
                                c={Colors.BluePrimary}
                              >
                                View Details
                              </Text>
                            </Center>
                            <Modal
                              onClose={ClosePlanDetailsModal}
                              centered
                              size="lg"
                              opened={isPlanDetailsModalOpened}
                              withCloseButton={false}
                            >
                              {/* Plan Details Modal */}
                              {plans?.data?.find((p) => p.id === selectedPlan) && (
                                <div className="plan-details-modal">
                                  <div className="plan-details-header">
                                    <div className="plan-details-title">
                                      <Title order={2} mb="xs">
                                        {plans.data.find((p) => p.id === selectedPlan)?.name}
                                      </Title>
                                      <Text size="lg" c="dimmed" mb="lg">
                                        {plans.data.find((p) => p.id === selectedPlan)?.tag}
                                      </Text>
                                    </div>
                                    <div className="plan-details-price">
                                      <Text fz={36} fw={700} c={Colors.PurplePrimary}>
                                        {getCurrencySymbol(
                                          allFatherContext?.locationInformation?.currency
                                        )}
                                        {getFinancialValueFromNumeric(
                                          plans.data.find((p) => p.id === selectedPlan)?.price || 0
                                        )}
                                      </Text>
                                      <Text fz={16} c="dimmed">
                                        per month
                                      </Text>
                                    </div>
                                  </div>

                                  <Divider my="xl" />

                                  <div className="plan-details-description">
                                    <Title order={3} mb="md">
                                      What's Included
                                    </Title>
                                    <Text size="lg" lineClamp={0} mb="xl">
                                      {plans.data.find((p) => p.id === selectedPlan)?.description}
                                    </Text>
                                  </div>

                                  <div className="plan-details-features">
                                    <Title order={3} mb="md">
                                      Plan Features
                                    </Title>
                                    <Stack gap="md">
                                      <Group gap="sm">
                                        <IconCheck size={20} color={Colors.PurplePrimary} />
                                        <Text>Access to all courses in this plan</Text>
                                      </Group>
                                      <Group gap="sm">
                                        <IconCheck size={20} color={Colors.PurplePrimary} />
                                        <Text>24/7 expert support</Text>
                                      </Group>
                                      <Group gap="sm">
                                        <IconCheck size={20} color={Colors.PurplePrimary} />
                                        <Text>Flexible learning schedule</Text>
                                      </Group>
                                      <Group gap="sm">
                                        <IconCheck size={20} color={Colors.PurplePrimary} />
                                        <Text>Certificate upon completion</Text>
                                      </Group>
                                      <Group gap="sm">
                                        <IconCheck size={20} color={Colors.PurplePrimary} />
                                        <Text>Lifetime access to course materials</Text>
                                      </Group>
                                    </Stack>
                                  </div>

                                  <Divider my="xl" />

                                  <div className="plan-details-actions">
                                    <Button
                                      fullWidth
                                      size="md"
                                      color={Colors.PurplePrimary}
                                      variant="filled"
                                      onClick={() => {
                                        ClosePlanDetailsModal();
                                        scrollToCourses();
                                      }}
                                    >
                                      Select This Plan
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </Modal>
                          </div>
                        </Card>
                      </MuiGrid>
                    ))}
                  </MuiGrid>
                </div>
              )}
            </Container>
          </div>

          {selectedPlan && (
            <div className="courses-section" ref={coursesSectionRef}>
              <Container size="lg">
                <div className="section-header">
                  <Title order={2}>{t("courses.coursesInPlan")}</Title>
                </div>

                {isLoadingCourses ? (
                  <MegaLoader size={50} />
                ) : (
                  <>
                    {courses?.data?.length && courses?.data?.length > 0 ? (
                      <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="md">
                        {courses?.data?.map((course) => (
                          <motion.div
                            key={course.id}
                            whileHover={{ scale: 1.02 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Card
                              className="course-card"
                              onClick={() => {
                                setSelectedCourse(course);
                                setIsModalOpen(true);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <div className="course-image">
                                <Image
                                  src={
                                    course.thumbnail ||
                                    course.images[0] ||
                                    "https://placehold.co/600x400"
                                  }
                                  alt={course.name}
                                />
                              </div>
                              <div className="course-content">
                                <Title order={3}>{course.name}</Title>
                                <Text size="sm" c={Colors.GrayTertiary} lineClamp={3}>
                                  {getEllipsisFromString(course.description, 100)}
                                </Text>
                              </div>
                            </Card>
                          </motion.div>
                        ))}
                      </SimpleGrid>
                    ) : (
                      <Text c="dimmed" ta="center" py="xl">
                        {t("courses.noCourses")}
                      </Text>
                    )}
                  </>
                )}
              </Container>
            </div>
          )}

          {/* Course Details Modal */}
          <Modal
            opened={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            size="lg"
            centered
            classNames={{
              header: "course-modal-header",
              body: "course-modal-body",
              content: "course-modal-content",
            }}
          >
            {selectedCourse && (
              <div className="course-modal">
                <div className="course-modal-hero">
                  <Image
                    src={
                      selectedCourse.thumbnail ||
                      selectedCourse.images[0] ||
                      "https://placehold.co/800x400"
                    }
                    alt={selectedCourse.name}
                    className="course-modal-image"
                  />
                  <div className="course-modal-overlay">
                    <Title order={1} className="course-modal-title">
                      {selectedCourse.name}
                    </Title>
                    <Group gap="md" className="course-modal-badges">
                      <Badge leftSection={<IconClock size={14} />} color="blue" variant="light">
                        8 weeks
                      </Badge>
                      <Badge leftSection={<IconUsers size={14} />} color="green" variant="light">
                        1.2k students
                      </Badge>
                      <Badge leftSection={<IconStar size={14} />} color="yellow" variant="light">
                        4.8 rating
                      </Badge>
                    </Group>
                  </div>
                </div>

                <div className="course-modal-content-wrapper">
                  <div className="course-modal-description">
                    <Title order={3} mb="md">
                      About This Course
                    </Title>
                    <Text size="lg" lineClamp={0}>
                      {selectedCourse.description}
                    </Text>
                  </div>

                  <Divider my="xl" />

                  <div className="course-modal-features">
                    <Title order={3} mb="md">
                      What You'll Learn
                    </Title>
                    <Stack gap="md">
                      <Group gap="sm">
                        <IconCheck size={20} color={Colors.PurplePrimary} />
                        <Text>Master the fundamentals of the subject</Text>
                      </Group>
                      <Group gap="sm">
                        <IconCheck size={20} color={Colors.PurplePrimary} />
                        <Text>Build real-world projects</Text>
                      </Group>
                      <Group gap="sm">
                        <IconCheck size={20} color={Colors.PurplePrimary} />
                        <Text>Access to expert support</Text>
                      </Group>
                    </Stack>
                  </div>

                  <Divider my="xl" />

                  <div className="course-modal-subscription">
                    <Title order={3} mb="md">
                      Subscription Access
                    </Title>
                    <Alert
                      icon={<IconCheck size={16} />}
                      color="blue"
                      variant="light"
                      title="Included in your subscription"
                    >
                      <Text size="sm">
                        This course is included in your selected plan. Subscribe to get unlimited
                        access to all courses in this plan.
                      </Text>
                    </Alert>
                  </div>
                </div>
              </div>
            )}
          </Modal>
        </motion.div>
      </div>
    </>
  );
};

export default PlansNoAuth;
